/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useCallback, useEffect, useMemo } from 'react';
import {
	Card,
	Typography,
	Link,
	Button,
	Stack,
	Box,
	Skeleton,
	useTheme,
	useMediaQuery,
} from '@mui/material';
import { formatDate } from '@/utils/constants';
import { useGetMsgFileHtmlQuery } from '@/features/files/filesApi';
import EngagementsAccordion from '../EngagementsAccordion';
import PreviewIcon from '@mui/icons-material/Preview';
import { useObjectContext } from '@/hooks/useObjectContext';
import parse, { domToReact } from 'html-react-parser';
import { safelyParseDate } from '@/utils/date';
import SafeHtmlContent from '../SafeHtmlContent';

export const EmailCard = ({ email }) => {
	const { dealRow, clientRow } = useObjectContext();

	const driveId = dealRow?.driveId ?? clientRow?.driveId;
	const clientFolderDriveId = dealRow?.clientFolderDriveId ?? clientRow?.clientFolderDriveId;

	const {
		data: emailMsgData,
		isLoading,
		isError,
	} = useGetMsgFileHtmlQuery(
		{
			id: email?.data?.id,
			downloadUrl: email?.data['@microsoft.graph.downloadUrl'],
			driveId,
			clientFolderDriveId,
			createdDateTime: email.data?.createdDateTime,
		},
		{ skip: !email?.data },
	);

	useEffect(
		() => console.log('EMAIL DOC DATA', email, 'EMAIL MSG DATA', emailMsgData),
		[email, emailMsgData],
	);

	const emailHtml = useMemo(() => {
		const body = (emailMsgData?.body ?? '').split(/\r\n|\n/g).map((r) => r.trim());

		// Array to hold the processed lines
		const processedLines = [];
		let emptyLineStreak = 0;

		for (const line of body) {
			if (line === '') {
				emptyLineStreak++;
				if (emptyLineStreak <= 3) {
					processedLines.push('\n');
				}
			} else {
				emptyLineStreak = 0;
				processedLines.push(line);
			}
		}

		return processedLines.join('').trim();
	}, [emailMsgData?.body]);

	const subject = useMemo(() => emailMsgData?.subject ?? '', [emailMsgData?.subject]);
	const recipients = useMemo(
		() =>
			(emailMsgData?.recipients ?? emailMsgData?.to?.value ?? []).map(
				(r) => r?.smtpAddress ?? r?.email ?? r?.address ?? '',
			),
		[emailMsgData?.recipients, emailMsgData?.to?.value],
	);

	const sender = useMemo(() => {
		const senderEmail =
			emailMsgData?.senderAddressType == 'EX'
				? (emailMsgData?.senderSmtpAddress ??
					emailMsgData?.creatorSMTPAddress ??
					emailMsgData?.lastModifierSMTPAddress ??
					emailMsgData?.sentRepresentingSmtpAddress ??
					emailMsgData?.inetAcctName ??
					emailMsgData?.senderEmail ??
					'')
				: (emailMsgData?.senderEmail ??
					emailMsgData?.senderSmtpAddress ??
					emailMsgData?.creatorSMTPAddress ??
					emailMsgData?.sentRepresentingSmtpAddress ??
					emailMsgData?.lastModifierSMTPAddress ??
					emailMsgData?.from?.text ??
					'');
		return senderEmail;
	}, [
		emailMsgData?.inetAcctName,
		emailMsgData?.senderSmtpAddress,
		emailMsgData?.creatorSMTPAddress,
		emailMsgData?.lastModifierSMTPAddress,
		emailMsgData?.sentRepresentingSmtpAddress,
		emailMsgData?.senderEmail,
		emailMsgData?.senderAddressType,
		emailMsgData?.from?.text,
	]);
	const date = useMemo(
		() =>
			safelyParseDate(
				emailMsgData?.messageDeliveryTime ??
					emailMsgData?.date ??
					email.data?.createdDateTime,
			),
		[emailMsgData?.messageDeliveryTime, emailMsgData?.date, email.data?.createdDateTime],
	);

	const lastUpdated = useMemo(
		() =>
			safelyParseDate(
				email.data?.lastModifiedDateTime ??
					emailMsgData?.lastModificationTime ??
					new Date(),
			),
		[emailMsgData?.lastModificationTime, email.data?.lastModifiedDateTime],
	);

	const NoteContent = useCallback(() => {
		if (typeof emailMsgData?.html === 'string' && emailMsgData.html.trim())
			return <SafeHtmlContent html={emailMsgData.html} />;
		return <Typography sx={{ whiteSpace: 'pre-line' }}>{emailHtml}</Typography>;
	}, [emailHtml, emailMsgData?.html]);

	return (
		<EngagementsAccordion
			title={
				<Stack>
					<Typography>
						<span style={{ fontWeight: 'bold' }}>
							Email{`${subject ? ` - ${subject}` : ''}`}
						</span>
						<span>
							<span style={{ margin: '0 0.5em' }}>{' | '}</span>
							<span style={{ fontWeight: '500' }}> {formatDate(date, true)} </span>
						</span>
					</Typography>
					{sender && (recipients ?? []).length > 0 && (
						<Typography variant='task_updated'>
							<span style={{ fontWeight: 'bold', marginRight: '0.25em' }}>
								{'From: '}
							</span>
							<span>{sender}</span>
							<span
								style={{
									fontWeight: 'bold',
									marginRight: '0.25em',
									marginLeft: '0.25em',
								}}
							>
								{' to: '}
							</span>
							<span>{recipients.join(', ')}</span>
						</Typography>
					)}
				</Stack>
			}
			// lastUpdated={lastUpdated}
			footerContent={
				<Link
					href={`${email?.data?.webUrl}?web=1`}
					target='_blank'
					rel='noopener noreferrer'
					sx={{ textDecorationLine: 'none' }}
				>
					<Stack direction='row' alignContent='center'>
						<PreviewIcon
							sx={(theme) => ({ color: theme.palette.primary.main, mr: '0.25em' })}
						/>
						{email.data.name}
					</Stack>
				</Link>
			}
			expandedContent={
				<Box width='100%'>
					{isLoading ? (
						<Skeleton variant='text' width='100%' />
					) : isError ? (
						<Typography>Error loading email content</Typography>
					) : (
						<NoteContent />
					)}
				</Box>
			}
		/>
	);
};
